/**
 * ProductivityMatrix — iter131 Phase 3
 * -----------------------------------------------------------------------------
 * Visual productivity dashboard comparing per-mechanic:
 *   Workshop Hours (clock-in → clock-off minus breaks)
 *   Allocated Hours (sum of quoted_labour_hours for JCs scheduled to mechanic)
 *   Produced Hours (sum of time_log session durations)
 *
 * Mechanics see ONLY their own row (server-enforced privacy gate).
 * SA / SM / FM see all mechanics in the workshop.
 *
 * Charts:
 *   1. Grouped Bar — per-mechanic totals comparison (W vs A vs P)
 *   2. Efficiency leaderboard (produced / workshop %)
 *   3. Drill-down: per-day breakdown for a selected mechanic
 */
import { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { CalendarDays, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function todayIso() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function daysAgoIso(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const PRESETS = [
  { key: 'today',   label: 'Today',        start: () => todayIso(),    end: () => todayIso() },
  { key: 'week',    label: 'Last 7 days',  start: () => daysAgoIso(6), end: () => todayIso() },
  { key: 'month',   label: 'Last 30 days', start: () => daysAgoIso(29), end: () => todayIso() },
  { key: 'mtd',     label: 'This month',   start: () => {
      const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10);
    }, end: () => todayIso() },
];

export default function ProductivityMatrix({ canEditRange = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [presetIdx, setPresetIdx] = useState(0); // default Today
  // iter140 — Custom date range. presetIdx === -1 means "use customStart/End".
  const [customStart, setCustomStart] = useState(todayIso());
  const [customEnd, setCustomEnd] = useState(todayIso());
  const [drillId, setDrillId] = useState(null);

  const range = useMemo(() => {
    if (presetIdx === -1) {
      return { start: customStart, end: customEnd, label: 'Custom' };
    }
    const p = PRESETS[presetIdx];
    return { start: p.start(), end: p.end(), label: p.label };
  }, [presetIdx, customStart, customEnd]);

  useEffect(() => {
    setLoading(true);
    const url = `${BACKEND_URL}/api/productivity-matrix?start_date=${range.start}&end_date=${range.end}`;
    fetch(url, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [range.start, range.end]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.rows
      .filter((r) => r.totals.workshop_hours > 0 || r.totals.allocated_hours > 0 || r.totals.produced_hours > 0)
      .map((r) => ({
        name: r.name,
        mechanic_id: r.mechanic_id,
        workshop: r.totals.workshop_hours,
        allocated: r.totals.allocated_hours,
        completed: r.totals.completed_hours || 0,
        produced: r.totals.produced_hours,
        efficiency: r.totals.efficiency_pct,
      }));
  }, [data]);

  const leaderboard = useMemo(() => {
    if (!data) return [];
    return [...chartData]
      .filter((r) => r.workshop > 0)
      .sort((a, b) => b.efficiency - a.efficiency)
      .slice(0, 10);
  }, [chartData, data]);

  const drilldown = useMemo(() => {
    if (!data || !drillId) return null;
    return data.rows.find((r) => r.mechanic_id === drillId);
  }, [data, drillId]);

  // iter175 — Lazily fetch all-time totals + leave for the drilled mechanic.
  const [allTime, setAllTime] = useState({});
  useEffect(() => {
    if (!drillId || allTime[drillId]) return;
    fetch(`${BACKEND_URL}/api/productivity-matrix/all-time?mechanic_id=${encodeURIComponent(drillId)}`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setAllTime((prev) => ({ ...prev, [drillId]: d })); })
      .catch(() => {});
  }, [drillId]);  

  return (
    <Card className="p-5 space-y-5" data-testid="productivity-matrix-card">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-indigo-600" />
            Productivity Matrix
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Workshop · Allocated · Completed · Produced hours · {range.label} · {range.start} → {range.end}
          </p>
        </div>
        {canEditRange && (
          <div className="flex gap-1 flex-wrap items-center" data-testid="productivity-range-presets">
            {PRESETS.map((p, i) => (
              <Button
                key={p.label}
                size="sm"
                variant={presetIdx === i ? 'default' : 'outline'}
                onClick={() => setPresetIdx(i)}
                data-testid={`range-preset-${p.label.replace(/\s+/g, '-').toLowerCase()}`}
                className="text-xs"
              >
                {p.label}
              </Button>
            ))}
            {/* iter140 — Custom date range. Toggle picks between presets and explicit range. */}
            <Button
              size="sm"
              variant={presetIdx === -1 ? 'default' : 'outline'}
              onClick={() => setPresetIdx(-1)}
              data-testid="range-preset-custom"
              className="text-xs"
            >
              Custom
            </Button>
            {presetIdx === -1 && (
              <div className="flex items-center gap-1 ml-1" data-testid="productivity-custom-range">
                <Input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="h-8 w-32 text-xs"
                  data-testid="productivity-custom-start"
                  aria-label="Start date"
                />
                <span className="text-slate-400 text-xs">→</span>
                <Input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="h-8 w-32 text-xs"
                  data-testid="productivity-custom-end"
                  aria-label="End date"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {loading && <p className="text-sm text-slate-400">Loading…</p>}
      {!loading && (!data || chartData.length === 0) && (
        <div className="rounded-lg bg-slate-50 border border-dashed border-slate-300 p-8 text-center text-slate-500" data-testid="productivity-empty-state">
          No productivity data in this range yet.
          <p className="text-xs mt-2 text-slate-400">
            Mechanics need to Clock-In and work on Job Cards to populate this matrix.
          </p>
        </div>
      )}

      {!loading && chartData.length > 0 && (
        <>
          {/* Grouped bar chart — main comparison */}
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">
              Workshop · Allocated · Completed · Produced (hours)
            </p>
            <ResponsiveContainer width="100%" height={Math.max(240, chartData.length * 60)}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ left: 80, right: 20, top: 5, bottom: 5 }}
                barCategoryGap="20%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tickFormatter={(v) => `${v}h`} fontSize={11} />
                <YAxis type="category" dataKey="name" fontSize={11} width={80} />
                <Tooltip
                  formatter={(v) => `${v.toFixed(1)}h`}
                  contentStyle={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="workshop" name="Workshop" fill="#94a3b8" radius={[0, 4, 4, 0]} />
                <Bar dataKey="allocated" name="Allocated" fill="#6366f1" radius={[0, 4, 4, 0]} />
                <Bar dataKey="completed" name="Completed" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                <Bar dataKey="produced" name="Produced" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Efficiency leaderboard */}
          {leaderboard.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="efficiency-leaderboard">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                Efficiency leaderboard (Produced ÷ Workshop)
              </p>
              <ResponsiveContainer width="100%" height={Math.max(200, leaderboard.length * 40)}>
                <BarChart
                  data={leaderboard}
                  layout="vertical"
                  margin={{ left: 80, right: 40, top: 5, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" domain={[0, (max) => Math.max(100, max + 10)]} tickFormatter={(v) => `${v}%`} fontSize={11} />
                  <YAxis type="category" dataKey="name" fontSize={11} width={80} />
                  <Tooltip formatter={(v) => `${v.toFixed(1)}%`} contentStyle={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }} />
                  <Bar dataKey="efficiency" name="Efficiency %" radius={[0, 4, 4, 0]}>
                    {leaderboard.map((entry, idx) => (
                      <Cell key={idx} fill={
                        entry.efficiency >= 100 ? '#059669' :
                        entry.efficiency >= 80 ? '#10b981' :
                        entry.efficiency >= 60 ? '#eab308' : '#ef4444'
                      } />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Per-mechanic table + drill-down */}
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="p-2 text-left">Mechanic</th>
                  <th className="p-2 text-right">Workshop</th>
                  <th className="p-2 text-right">Allocated</th>
                  <th className="p-2 text-right">Completed</th>
                  <th className="p-2 text-right">Produced</th>
                  <th className="p-2 text-right">Eff %</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((r) => (
                  <tr key={r.mechanic_id} className="border-t hover:bg-slate-50" data-testid={`matrix-row-${r.mechanic_id}`}>
                    <td className="p-2 font-medium">{r.name}</td>
                    <td className="p-2 text-right tabular-nums">{r.workshop.toFixed(1)}h</td>
                    <td className="p-2 text-right tabular-nums text-indigo-700">{r.allocated.toFixed(1)}h</td>
                    <td className="p-2 text-right tabular-nums text-sky-700">{r.completed.toFixed(1)}h</td>
                    <td className="p-2 text-right tabular-nums font-semibold text-emerald-700">{r.produced.toFixed(1)}h</td>
                    <td className={`p-2 text-right tabular-nums font-bold ${
                      r.efficiency >= 100 ? 'text-emerald-700' :
                      r.efficiency >= 80 ? 'text-amber-700' : 'text-rose-700'
                    }`}>{r.efficiency.toFixed(0)}%</td>
                    <td className="p-2">
                      <button
                        onClick={() => setDrillId(drillId === r.mechanic_id ? null : r.mechanic_id)}
                        className="text-indigo-600 hover:text-indigo-800 text-xs flex items-center gap-1"
                        data-testid={`drill-${r.mechanic_id}`}
                      >
                        <ChevronDown
                          className={`h-3 w-3 transition-transform ${drillId === r.mechanic_id ? 'rotate-180' : ''}`}
                        />
                        Days
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Drill-down: per-day breakdown */}
          {drilldown && drilldown.days && drilldown.days.length > 0 && (
            <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/30 p-3" data-testid="drilldown-panel">
              <p className="text-xs font-bold text-indigo-900 mb-2">
                {drilldown.name} — daily breakdown
              </p>

              {/* iter175 — All-time totals + leave strip */}
              {allTime[drillId] && (
                <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="drilldown-all-time">
                  {[
                    { k: 'workshop_hours', label: 'Workshop', cls: 'text-slate-700' },
                    { k: 'allocated_hours', label: 'Allocated', cls: 'text-indigo-700' },
                    { k: 'completed_hours', label: 'Completed', cls: 'text-sky-700' },
                    { k: 'produced_hours', label: 'Produced', cls: 'text-emerald-700' },
                    { k: 'efficiency_pct', label: 'Efficiency', cls: 'text-amber-700', suffix: '%' },
                    { k: 'total_leave_hours', label: 'Leave', cls: 'text-rose-700' },
                  ].map((t) => (
                    <div key={t.k} className="rounded-md border border-indigo-200 bg-white px-2 py-1.5 text-center">
                      <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">All-time {t.label}</p>
                      <p className={`text-sm font-extrabold tabular-nums ${t.cls}`} data-testid={`all-time-${t.k}`}>
                        {Number(allTime[drillId][t.k] || 0).toFixed(1)}{t.suffix || 'h'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {allTime[drillId] && Object.keys(allTime[drillId].leave_by_type || {}).length > 0 && (
                <div className="mb-3 flex flex-wrap gap-1.5" data-testid="drilldown-leave-breakdown">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold self-center">Leave taken:</span>
                  {Object.entries(allTime[drillId].leave_by_type).map(([type, hrs]) => (
                    <span key={type} className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 px-2 py-0.5 text-[10px] font-semibold capitalize">
                      {type.replace(/_/g, ' ')}: {Number(hrs).toFixed(1)}h
                    </span>
                  ))}
                </div>
              )}
              <ResponsiveContainer width="100%" height={Math.max(180, drilldown.days.length * 35)}>
                <BarChart
                  data={drilldown.days}
                  margin={{ left: 5, right: 20, top: 5, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis tickFormatter={(v) => `${v}h`} fontSize={11} />
                  <Tooltip
                    formatter={(v) => `${v.toFixed(1)}h`}
                    contentStyle={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="workshop_hours" name="Workshop" fill="#94a3b8" />
                  <Bar dataKey="allocated_hours" name="Allocated" fill="#6366f1" />
                  <Bar dataKey="completed_hours" name="Completed" fill="#0ea5e9" />
                  <Bar dataKey="produced_hours" name="Produced" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
              {/* iter163 — Numeric daily table so SM/mechanic can eyeball
                  exact hour values (chart is visual, table is precise).
                  Matches the JC Show-Breakdown column order:
                  Allocated / Completed / Produced. */}
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-indigo-100 text-indigo-800">
                    <tr>
                      <th className="p-1.5 text-left">Date</th>
                      <th className="p-1.5 text-center">Clock In</th>
                      <th className="p-1.5 text-center">Clock Out</th>
                      <th className="p-1.5 text-right">Workshop</th>
                      <th className="p-1.5 text-right text-indigo-700">Allocated</th>
                      <th className="p-1.5 text-right text-sky-700">Completed</th>
                      <th className="p-1.5 text-right text-emerald-700">Produced</th>
                      <th className="p-1.5 text-right">Eff %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldown.days.map((d) => (
                      <tr key={d.date} className={`border-t border-indigo-100 ${d.on_leave ? 'bg-rose-50/60' : ''}`} data-testid={`drilldown-day-${d.date}`}>
                        <td className="p-1.5 tabular-nums whitespace-nowrap">
                          {d.date}
                          {d.on_leave && (
                            <span className="ml-1.5 inline-flex items-center rounded-full bg-rose-100 text-rose-700 px-1.5 py-0.5 text-[9px] font-semibold capitalize" data-testid={`leave-badge-${d.date}`}>
                              {(d.leave_type || 'leave').replace(/_/g, ' ')}
                            </span>
                          )}
                        </td>
                        <td className="p-1.5 text-center tabular-nums text-slate-600">{d.clock_in_local || '—'}</td>
                        <td className="p-1.5 text-center tabular-nums text-slate-600">
                          {d.clock_out_local || '—'}
                          {d.clock_out_reason === 'auto_5pm' && (
                            <span className="ml-1 text-[8px] text-amber-500 font-bold" title="Auto clock-off at 5pm">AUTO</span>
                          )}
                        </td>
                        <td className="p-1.5 text-right tabular-nums">{Number(d.workshop_hours || 0).toFixed(1)}h</td>
                        <td className="p-1.5 text-right tabular-nums text-indigo-700">{Number(d.allocated_hours || 0).toFixed(1)}h</td>
                        <td className="p-1.5 text-right tabular-nums text-sky-700">{Number(d.completed_hours || 0).toFixed(1)}h</td>
                        <td className="p-1.5 text-right tabular-nums font-semibold text-emerald-700">{Number(d.produced_hours || 0).toFixed(1)}h</td>
                        <td className="p-1.5 text-right tabular-nums font-bold">{Number(d.efficiency_pct || 0).toFixed(0)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
